import { SupabankLogo } from "@/components/brand/SupabankLogo";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuItem,
    SidebarTrigger,
    useSidebar
} from "@/components/ui/sidebar";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTelegram } from "@/hooks/useTelegram";
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import {
    Activity,
    Building2,
    HelpCircle,
    LogOut,
    Settings,
    Wallet
} from "lucide-react";
import { Link, NavLink, useLocation } from "react-router-dom";

interface NavItem {
  id: string;
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  enabled: boolean;
  badge?: {
    icon: React.ComponentType<{ className?: string }>;
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
    tooltip: string;
  } | null;
  actionIndicator?: 'pending' | 'complete' | null;
}

export interface UserSidebarProps {
  className?: string;
  canCreateAddress?: boolean;
  onSignOut?: () => Promise<void> | void;
  onNavigateHelp?: () => void;
}

// Navigation configuration: full menu always visible; pages handle onboarding when canCreateAddress is false
const getNavItems = (_canCreateAddress: boolean): { title: string; items: NavItem[] }[] => {
  const items: { title: string; items: NavItem[] }[] = [];

  // Finance section: always show so nav is full; Wallet/Activity/Send pages show onboarding if not verified
  items.push({
    title: "Finance",
    items: [
      {
        id: "wallet",
        title: "SupaWallet",
        url: "/dashboard/wallet",
        icon: Wallet,
        description: "Balances and transactions",
        enabled: true
      },
      {
        id: "external-accounts",
        title: "External Accounts",
        url: "/dashboard/send",
        icon: Building2,
        description: "Bank accounts & crypto wallets",
        enabled: true
      },
      {
        id: "activity",
        title: "Activity",
        url: "/dashboard/activity",
        icon: Activity,
        description: "Transaction history",
        enabled: true
      }
    ]
  });

  // Account section
  items.push({
    title: "Account",
    items: [
      // Profile removed - consolidated into Settings
      // Users can access profile information via Settings → Profile tab
      {
        id: "settings",
        title: "Settings",
        url: "/dashboard/settings",
        icon: Settings,
        description: "Preferences",
        enabled: true
      }
    ]
  });

  return items;
};

export function UserSidebar({
  className,
  canCreateAddress = false,
  onSignOut,
  onNavigateHelp
}: UserSidebarProps) {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const sections = getNavItems(canCreateAddress);

  // Telegram-aware: detect if running in Telegram WebApp
  // Hide Sign Out in Telegram (user exits via Telegram app)
  let isInTelegram = false;
  let hapticFeedback: { selection: () => void } | null = null;
  try {
    const telegram = useTelegram();
    isInTelegram = telegram.isInTelegram;
    hapticFeedback = telegram.hapticFeedback;
  } catch {
    // Not in TelegramProvider context
  }

  // In Telegram, sign out is handled by closing the Mini App
  const showSignOut = onSignOut && !isInTelegram;

  return (
    <Sidebar collapsible="icon" className={className}>
      {/* Header - Premium spacing matching CardHeader, консистентный с BalanceCard */}
      <div className={cn(
        "flex items-center border-b transition-all",
        semanticTokens.motion.standard,
        "border-border/60", // Консистентно с BalanceCard (border-border/60)
        collapsed ? "flex-col gap-2 px-2 py-3" : cn("justify-between", semanticTokens.spacing.lg, "px-6 py-5")
      )}>
        <Link to="/dashboard" className="flex items-center">
          <SupabankLogo
            variant={collapsed ? 'icon' : 'full'}
            size="sm"
          />
        </Link>
        {/* SidebarTrigger - показываем всегда, в collapsed режиме под логотипом */}
        <Tooltip>
          <TooltipTrigger asChild>
            <SidebarTrigger className={cn(
              collapsed ? "mt-1" : "ml-auto"
            )} />
          </TooltipTrigger>
          <TooltipContent side="right">
            {collapsed ? "Expand sidebar" : "Collapse sidebar"}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Content - Premium spacing matching CardContent */}
      <TooltipProvider>
        <SidebarContent className={cn(
          semanticTokens.spacing.lg.replace('p-', 'py-'),
          "px-6",
          "group-data-[collapsible=icon]:px-2"
        )}>
          {sections.map((section) => (
            <SidebarGroup key={section.title}>
              <SidebarGroupLabel>
                {!collapsed && section.title}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => {
                    // Active state logic for different items
                    let isActive = false;
                    if (item.id === 'wallet') {
                      // Wallet is active on /dashboard or /dashboard/wallet (but not /dashboard/send)
                      isActive = location.pathname === '/dashboard' ||
                        (location.pathname.startsWith('/dashboard/wallet') &&
                          !location.pathname.startsWith('/dashboard/send'));
                    } else {
                      isActive = location.pathname.startsWith(item.url);
                    }

                    const handleNavClick = () => {
                      hapticFeedback?.selection();
                    };
                    const menuItem = (
                      <NavLink
                        to={item.url}
                        end={item.id === 'wallet' && location.pathname === '/dashboard'}
                        onClick={handleNavClick}
                        className={cn(
                          "flex items-center gap-3 rounded-md px-3 py-2.5",
                          semanticTokens.typography.label,
                          semanticTokens.motion.standard,
                          semanticTokens.radius.md,
                          "transition-all duration-240 ease-standard",
                          // Enhanced hover: subtle lift + background
                          "hover:bg-accent/50 hover:text-accent-foreground hover:translate-x-0.5",
                          // Active press feedback
                          "active:scale-[0.98] active:bg-accent/60",
                          // Active state
                          isActive
                            ? "bg-primary/10 text-primary font-semibold"
                            : "text-muted-foreground",
                          collapsed && "justify-center px-2",
                          "min-h-[44px] sm:min-h-[40px]",
                          semanticTokens.focus.ring,
                          // Reduced motion
                          "motion-reduce:transform-none"
                        )}
                      >
                        <item.icon className={cn(
                          "h-5 w-5 flex-shrink-0 transition-transform duration-240",
                          isActive && "text-primary scale-110"
                        )} />
                        {!collapsed && <span>{item.title}</span>}
                      </NavLink>
                    );

                    return (
                      <SidebarMenuItem key={item.id}>
                        {collapsed ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              {menuItem}
                            </TooltipTrigger>
                            <TooltipContent side="right" align="center">
                              {item.title}
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          menuItem
                        )}
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </SidebarContent>

        {/* Footer - Help and Logout внизу sidebar (outside SidebarContent for proper layout) */}
        <SidebarFooter className={cn(
          "border-t pt-4 border-border/60",
          "px-6 group-data-[collapsible=icon]:px-2"
        )}>
          <SidebarMenu>
            {onNavigateHelp && (() => {
              const isActive = location.pathname === '/dashboard/help';
              const helpItem = (
                <NavLink
                  to="/dashboard/help"
                  onClick={(e) => {
                    e.preventDefault();
                    onNavigateHelp();
                  }}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2.5 w-full text-left",
                    semanticTokens.typography.label,
                    semanticTokens.motion.standard,
                    semanticTokens.radius.md,
                    "transition-all duration-240 ease-standard",
                    "hover:bg-accent/50 hover:text-accent-foreground hover:translate-x-0.5",
                    "active:scale-[0.98] active:bg-accent/60",
                    isActive
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-muted-foreground",
                    collapsed && "justify-center px-2",
                    "min-h-[44px] sm:min-h-[40px]",
                    semanticTokens.focus.ring,
                    "motion-reduce:transform-none"
                  )}
                >
                  <HelpCircle className={cn(
                    "h-5 w-5 flex-shrink-0 transition-transform duration-240",
                    isActive && "text-primary scale-110"
                  )} />
                  {!collapsed && <span>Help</span>}
                </NavLink>
              );

              return (
                <SidebarMenuItem>
                  {collapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {helpItem}
                      </TooltipTrigger>
                      <TooltipContent side="right" align="center">
                        Help
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    helpItem
                  )}
                </SidebarMenuItem>
              );
            })()}

            {showSignOut && (() => {
              const handleSignOut = () => {
                hapticFeedback?.selection();
                onSignOut?.();
              };
              const logoutItem = (
                <button
                  onClick={handleSignOut}
                  className={cn(
                    "flex items-center gap-3 rounded-md px-3 py-2.5 w-full text-left",
                    semanticTokens.typography.label,
                    semanticTokens.motion.standard,
                    semanticTokens.radius.md,
                    "transition-all duration-240 ease-standard",
                    "hover:bg-accent/50 hover:text-accent-foreground hover:translate-x-0.5",
                    "active:scale-[0.98] active:bg-accent/60",
                    "text-muted-foreground",
                    collapsed && "justify-center px-2",
                    "min-h-[44px] sm:min-h-[40px]",
                    semanticTokens.focus.ring,
                    "motion-reduce:transform-none"
                  )}
                  aria-label="Logout from Supabank"
                >
                  <LogOut className="h-5 w-5 flex-shrink-0 transition-transform duration-240" />
                  {!collapsed && <span>Logout</span>}
                </button>
              );

              return (
                <SidebarMenuItem>
                  {collapsed ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {logoutItem}
                      </TooltipTrigger>
                      <TooltipContent side="right" align="center">
                        Logout
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    logoutItem
                  )}
                </SidebarMenuItem>
              );
            })()}
          </SidebarMenu>
        </SidebarFooter>
      </TooltipProvider>
    </Sidebar>
  );
}
